import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Event, Agent, Workflow, AGENT_COLORS, EVENT_COLORS } from "@/shared/types";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { ResizablePanel } from "./ResizablePanel";
import { ChatClient } from "./ChatClient";

interface WaterfallVisualizationProps {
  events: Event[];
  agents: Agent[];
  currentTime: number;
  zoomX: number;
  zoomY: number;
  onZoomXChange?: (_zoom: number) => void;
  onZoomYChange?: (_zoom: number) => void;
  onEventClick?: (_event: Event) => void;
  onSendMessage?: (_message: string, _targetAgents: string[]) => void;
  onAgentClick?: (_agentId: string) => void;
  showChatClient?: boolean;
  chatClientWidth?: number;
  onChatClientWidthChange?: (_width: number) => void;
  // sessions management
  sessions?: Workflow[];
  onLoadSession?: (_workflowId: string) => void;
  onNewChat?: () => void;
  isLoadingSessions?: boolean;
  className?: string;
}

export interface WaterfallVisualizationRef {
  centralizeEvent: (eventId: string) => void;
}

interface Point {
  x: number;
  y: number;
}

export const WaterfallVisualization = forwardRef<WaterfallVisualizationRef, WaterfallVisualizationProps>(({
  events,
  agents,
  currentTime,
  zoomX,
  zoomY,
  onZoomXChange,
  onZoomYChange,
  onEventClick,
  onSendMessage,
  onAgentClick,
  showChatClient = true,
  chatClientWidth = 350,
  onChatClientWidthChange,
  sessions,
  onLoadSession,
  onNewChat,
  isLoadingSessions,
  className = "",
}, ref) => {

  // Debug agent structure
  agents.forEach((agent, index) => {
    console.log(`Agent ${index}:`, {
      id: agent.id,
      type: agent.type,
      status: agent.status,
      fullAgent: agent,
    });
  });

  // Debug event-agent mapping
  events.forEach((event, index) => {
    const agentIndex = agents.findIndex((a) => a.id === event.agentId);
    console.log(`Event ${index} (${event.type}):`, {
      eventAgentId: event.agentId,
      foundAgentIndex: agentIndex,
      agentExists: agentIndex !== -1,
      event: event,
    });
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const lastEventCountRef = useRef(0);

  // State for interaction
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [panOffset, setPanOffset] = useState<Point>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Point>({ x: 0, y: 0 });
  const [hoveredEvent, setHoveredEvent] = useState<Event | null>(null);
  const [mousePosition, setMousePosition] = useState<Point>({ x: 0, y: 0 });
  const [autoScroll, setAutoScroll] = useState(true);
  const [actualChatWidth, setActualChatWidth] = useState(chatClientWidth);
  const [highlightedEventId, setHighlightedEventId] = useState<string | null>(
    null,
  );
  const [centeringStatus, setCenteringStatus] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [agentQuickMessage, setAgentQuickMessage] = useState<string>("");

  // Constants
  const MIN_LANE_HEIGHT = 40;
  // Remove MAX_LANE_HEIGHT limit for unlimited scaling
  const AGENT_WIDTH = 150;
  const TIMELINE_HEIGHT = 40;
  const MIN_EVENT_HEIGHT = 24;
  // Remove MAX_EVENT_HEIGHT limit for unlimited scaling
  const PIXELS_PER_SECOND = 80 * (zoomX / 100);
  const LINE_HEIGHT = 16;
  const CHAT_CLIENT_WIDTH = showChatClient ? actualChatWidth : 0;

  // Calculate max time for timeline with extended range
  const maxTime = useMemo(() => {
    if (events.length === 0) return 10;
    const lastEventTime = Math.max(...events.map((e) => e.timestamp));
    return Math.ceil(lastEventTime * 1.2); // Add 20% buffer
  }, [events]);

  // Show all events for real-time rendering
  const visibleEvents = useMemo(() => {
    return events;
  }, [events]);

  // Calculate dynamic heights for events based on content
  const getEventHeight = useCallback((content: string, maxWidth: number) => {
    const charWidth = 7; // Approximate character width in pixels
    const charsPerLine = Math.floor((maxWidth - 16) / charWidth); // Account for padding
    const lines = Math.ceil(content.length / charsPerLine);
    // Add more padding to event height calculation
    const height = Math.max(MIN_EVENT_HEIGHT, lines * LINE_HEIGHT + 16);
    return height;
  }, []);

  // Calculate dynamic lane heights based on events in each lane
  const getLaneHeight = useCallback(
    (agentId: string) => {
      const agentEvents = events.filter((e) => e.agentId === agentId);
      if (agentEvents.length === 0) return MIN_LANE_HEIGHT;

      const maxEventHeight = Math.max(
        ...agentEvents.map((event) => {
          const eventWidth = Math.max(
            100,
            Math.min(400, event.content.length * 6),
          );
          return getEventHeight(event.content, eventWidth);
        }),
      );

      // Add much more padding to ensure events don't overflow - at least 60px above and below
      const laneHeight = Math.max(MIN_LANE_HEIGHT, maxEventHeight + 120);
      return laneHeight; // No maximum limit
    },
    [events, getEventHeight],
  );

  // Build agent tree structure based on agent types (orchestrator -> others)
  const agentTree = useMemo(() => {
    type TreeNode = {
      agent: Agent;
      children: TreeNode[];
    };

    const tree: TreeNode[] = [];

    // Find orchestrator (root)
    const orchestrator = agents.find((agent) => agent.type === "orchestrator");

    if (orchestrator) {
      // All non-orchestrator agents are children of orchestrator
      const children: TreeNode[] = agents
        .filter((agent) => agent.type !== "orchestrator")
        .map((agent) => ({ agent, children: [] }));

      tree.push({ agent: orchestrator, children });
    } else {
      // If no orchestrator, treat all agents as root level
      agents.forEach((agent) => {
        tree.push({ agent, children: [] });
      });
    }

    return tree;
  }, [agents]);

  // Flatten tree for position mapping with proper indentation
  const flattenedAgents = useMemo(() => {
    type TreeNode = {
      agent: Agent;
      children: TreeNode[];
    };

    const flattened: Array<{
      agent: Agent;
      depth: number;
      isExpanded: boolean;
      parentId: string | null;
    }> = [];

    const flatten = (node: TreeNode, depth: number, parentId: string | null) => {
      flattened.push({ agent: node.agent, depth, isExpanded: true, parentId });
      node.children.forEach((child) => flatten(child, depth + 1, node.agent.id));
    };

    agentTree.forEach((rootNode) => flatten(rootNode, 0, null));
    return flattened;
  }, [agentTree]);

  // Agent position mapping with cumulative heights and tree structure
  const agentPositions = useMemo(() => {
    const positions: Record<
      string,
      { index: number; y: number; height: number; depth: number }
    > = {};
    let currentY = TIMELINE_HEIGHT;

    flattenedAgents.forEach((item, index) => {
      const height = getLaneHeight(item.agent.id);
      positions[item.agent.id] = {
        index,
        y: currentY,
        height,
        depth: item.depth,
      };
      currentY += height;
    });
    return positions;
  }, [flattenedAgents, getLaneHeight]);
  // Get current agent status based on latest events
  const getAgentCurrentStatus = useCallback(
    (agentId: string) => {
      // Find the most recent event for this agent
      const agentEvents = events
        .filter((e) => e.agentId === agentId)
        .sort((a, b) => b.timestamp - a.timestamp);

      if (agentEvents.length === 0) return { status: "idle", color: "#6b7280" };

      const latestEvent = agentEvents[0];
      const now = Date.now() / 1000; // Convert to seconds
      const timeSinceLastEvent = now - latestEvent.timestamp;

      // If event is very recent (within 2 seconds), show as active
      if (timeSinceLastEvent < 2) {
        if (
          latestEvent.status === "running" ||
          latestEvent.type === "toolCall"
        ) {
          return { status: "running", color: "#3b82f6" };
        } else if (latestEvent.type === "message") {
          return { status: "active", color: "#f59e0b" };
        }
      }

      // Determine status based on event type and status
      if (
        latestEvent.status === "complete" ||
        latestEvent.type === "toolResult"
      ) {
        return { status: "complete", color: "#10b981" };
      } else if (
        latestEvent.status === "error" ||
        latestEvent.type === "error"
      ) {
        return { status: "error", color: "#ef4444" };
      } else if (
        latestEvent.status === "running" ||
        latestEvent.type === "toolCall"
      ) {
        return { status: "busy", color: "#8b5cf6" };
      } else {
        return { status: "idle", color: "#6b7280" };
      }
    },
    [events],
  );

  // Auto-scroll to follow latest events
  useEffect(() => {
    if (!autoScroll || events.length <= lastEventCountRef.current) return;

    const latestEvent = events[events.length - 1];
    if (latestEvent) {
      const targetX = -(
        latestEvent.timestamp * PIXELS_PER_SECOND -
        canvasSize.width +
        AGENT_WIDTH +
        200
      );
      setPanOffset((prev) => ({ ...prev, x: Math.min(0, targetX) }));
    }

    lastEventCountRef.current = events.length;
  }, [events, canvasSize.width, PIXELS_PER_SECOND, autoScroll]);

  // Handle chat width change
  const handleChatWidthChange = useCallback(
    (width: number) => {
      setActualChatWidth(width);
      onChatClientWidthChange?.(width);
    },
    [onChatClientWidthChange],
  );

  // Centralize and zoom to specific event
  const centralizeEvent = useCallback(
    (eventId: string) => {
      const event = events.find((e) => e.id === eventId);
      if (!event) return;

      const agentPosition = agentPositions[event.agentId];
      if (!agentPosition) return;

      // Highlight the event and show status
      setHighlightedEventId(eventId);
      setCenteringStatus("Centering event in diagram...");

      // Disable auto-scroll when manually centering
      setAutoScroll(false);

      // Calculate target position to center the event horizontally in the screen
      const targetEventX = event.timestamp * PIXELS_PER_SECOND;
      const centerTarget = canvasSize.width * 0.5; // center of the canvas
      const targetPanX = Math.min(
        0,
        centerTarget - targetEventX - AGENT_WIDTH,
      );

      // Calculate vertical center for the event
      const eventCenterY = agentPosition.y + agentPosition.height / 2;
      const screenCenterY = canvasSize.height / 2;
      const targetPanY = screenCenterY - eventCenterY - TIMELINE_HEIGHT;

      // Smoothly animate to the target position
      const currentPanX = panOffset.x;
      const currentPanY = panOffset.y;
      const deltaX = targetPanX - currentPanX;
      const deltaY = targetPanY - currentPanY;

      // Simple easing animation
      let progress = 0;
      const duration = 500; // 500ms animation
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        progress = Math.min((currentTime - startTime) / duration, 1);

        // Ease-out cubic function
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        setPanOffset({
          x: currentPanX + deltaX * easeProgress,
          y: currentPanY + deltaY * easeProgress,
        });

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);

      // Optional: Adjust zoom for better visibility
      const optimalZoomX = Math.max(75, Math.min(150, zoomX));
      const optimalZoomY = Math.max(120, Math.min(250, zoomY));

      if (Math.abs(optimalZoomX - zoomX) > 10) {
        onZoomXChange?.(optimalZoomX);
      }
      if (Math.abs(optimalZoomY - zoomY) > 20) {
        onZoomYChange?.(optimalZoomY);
      }

      // Clear highlight and status after animation
      setTimeout(() => {
        setCenteringStatus(null);
      }, 600);

      setTimeout(() => {
        setHighlightedEventId(null);
      }, 3000);
    },
    [
      events,
      agentPositions,
      PIXELS_PER_SECOND,
      canvasSize,
      zoomX,
      zoomY,
      onZoomXChange,
      onZoomYChange,
      panOffset,
    ],
  );

  // Expose centralizeEvent function via ref
  useImperativeHandle(ref, () => ({
    centralizeEvent,
  }), [centralizeEvent]);

  // Resize handler
  const handleResize = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const newSize = {
      width: rect.width - CHAT_CLIENT_WIDTH, // Subtract chat width from canvas
      height: rect.height, // Use full container height
    };

    setCanvasSize(newSize);

    if (canvasRef.current) {
      const dpr = window.devicePixelRatio || 1;
      canvasRef.current.width = newSize.width * dpr;
      canvasRef.current.height = newSize.height * dpr;
      canvasRef.current.style.width = `${newSize.width}px`;
      canvasRef.current.style.height = `${newSize.height}px`;

      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        ctx.scale(dpr, dpr);
      }
    }
  }, [CHAT_CLIENT_WIDTH]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (e.button !== 0) return; // Only left click

      setIsPanning(true);
      setAutoScroll(false); // Disable auto-scroll when user manually pans
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      e.preventDefault();
    },
    [panOffset],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      setMousePosition({ x: mouseX, y: mouseY });

      if (isPanning) {
        setPanOffset({
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        });
        return;
      }

      // Check for event hover
      const adjustedX = mouseX - panOffset.x - AGENT_WIDTH;
      const adjustedY = mouseY - panOffset.y - TIMELINE_HEIGHT;

      let foundEvent: Event | null = null;

      for (const event of visibleEvents) {
        const agentPosition = agentPositions[event.agentId];
        if (!agentPosition) continue;

        const eventX = event.timestamp * PIXELS_PER_SECOND;
        const eventWidth = Math.max(
          100,
          Math.min(400, event.content.length * 6),
        );
        const eventHeight = getEventHeight(event.content, eventWidth);
        // Better centering for hover detection
        const verticalPadding = Math.max(
          20,
          (agentPosition.height - eventHeight) / 2,
        );
        const eventY = agentPosition.y - TIMELINE_HEIGHT + verticalPadding;

        if (
          adjustedX >= eventX &&
          adjustedX <= eventX + eventWidth &&
          adjustedY >= eventY &&
          adjustedY <= eventY + eventHeight
        ) {
          foundEvent = event;
          break;
        }
      }

      setHoveredEvent(foundEvent);
    },
    [
      isPanning,
      panStart,
      panOffset,
      visibleEvents,
      agentPositions,
      PIXELS_PER_SECOND,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPanning(false);
    setHoveredEvent(null);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isPanning) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Detect click on agent label area
      if (mouseX <= AGENT_WIDTH) {
        // Find which agent row was clicked
        for (const item of flattenedAgents) {
          const pos = agentPositions[item.agent.id];
          if (!pos) continue;
          const y = pos.y + panOffset.y;
          if (mouseY >= y && mouseY <= y + pos.height) {
            setSelectedAgentId(item.agent.id);
            // Call onAgentClick if provided
            if (onAgentClick) {
              onAgentClick(item.agent.id);
            }
            return;
          }
        }
      }

      // Click on event
      if (hoveredEvent && onEventClick) {
        onEventClick(hoveredEvent);
      }
    },
    [isPanning, hoveredEvent, onEventClick, onAgentClick, flattenedAgents, agentPositions, panOffset],
  );

  // Handle trackpad/mouse wheel zoom with proper mouse-centered behavior
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      setAutoScroll(false); // Disable auto-scroll when zooming

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Get mouse position relative to canvas
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Use deltaY for zoom factor with better sensitivity
      const wheelDelta = -e.deltaY;
      // Reduced sensitivity to make zoom less jumpy
      const zoomSensitivity = 0.0004;
      const zoomFactor = 1 + wheelDelta * zoomSensitivity;

      if (e.ctrlKey || e.metaKey) {
        // Zoom both axes when holding Ctrl/Cmd
        const newZoomX = Math.max(25, Math.min(400, zoomX * zoomFactor));
        const newZoomY = Math.max(50, Math.min(400, zoomY * zoomFactor));

        // Calculate world coordinates under mouse cursor before zoom
        const worldTimeAtMouse =
          (mouseX - panOffset.x - AGENT_WIDTH) / PIXELS_PER_SECOND;
        const worldYAtMouse = mouseY - panOffset.y - TIMELINE_HEIGHT;

        // Calculate new pixels per second after zoom
        const newPixelsPerSecond = 80 * (newZoomX / 100);

        // Calculate where that world position would appear after zoom
        const newScreenTimePos =
          AGENT_WIDTH + worldTimeAtMouse * newPixelsPerSecond;
        const newScreenYPos =
          TIMELINE_HEIGHT + worldYAtMouse * (newZoomY / zoomY);

        // Calculate the adjustment needed to keep mouse position fixed
        const panAdjustmentX = mouseX - newScreenTimePos;
        const panAdjustmentY = mouseY - newScreenYPos;

        // Apply zoom changes
        onZoomXChange?.(Math.round(newZoomX));
        onZoomYChange?.(Math.round(newZoomY));

        // Adjust pan to keep content under mouse cursor
        setPanOffset((prev) => ({
          x: Math.min(0, prev.x + panAdjustmentX), // Prevent panning too far right
          y: prev.y + panAdjustmentY,
        }));
      } else if (e.shiftKey) {
        // Horizontal zoom only when holding Shift
        const newZoomX = Math.max(25, Math.min(400, zoomX * zoomFactor));

        // Calculate world time coordinate under mouse cursor
        const worldTimeAtMouse =
          (mouseX - panOffset.x - AGENT_WIDTH) / PIXELS_PER_SECOND;

        // Calculate new pixels per second
        const newPixelsPerSecond = 80 * (newZoomX / 100);

        // Calculate where that time would appear after zoom
        const newScreenTimePos =
          AGENT_WIDTH + worldTimeAtMouse * newPixelsPerSecond;

        // Calculate adjustment to keep time under mouse cursor
        const panAdjustmentX = mouseX - newScreenTimePos;

        // Apply zoom change
        onZoomXChange?.(Math.round(newZoomX));

        // Adjust horizontal pan only
        setPanOffset((prev) => ({
          x: Math.min(0, prev.x + panAdjustmentX),
          y: prev.y,
        }));
      } else {
        // Vertical zoom by default (like a DAW)
        const newZoomY = Math.max(50, Math.min(400, zoomY * zoomFactor));

        // Calculate world Y coordinate under mouse cursor
        const worldYAtMouse = mouseY - panOffset.y - TIMELINE_HEIGHT;

        // Calculate where that Y position would appear after zoom
        const newScreenYPos =
          TIMELINE_HEIGHT + worldYAtMouse * (newZoomY / zoomY);

        // Calculate adjustment to keep Y position under mouse cursor
        const panAdjustmentY = mouseY - newScreenYPos;

        // Apply zoom change
        onZoomYChange?.(Math.round(newZoomY));

        // Adjust vertical pan only
        setPanOffset((prev) => ({
          x: prev.x,
          y: prev.y + panAdjustmentY,
        }));
      }
    },
    [zoomX, zoomY, onZoomXChange, onZoomYChange, panOffset, PIXELS_PER_SECOND],
  );

  // Drawing functions
  const drawBackground = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.fillStyle = "#1e1e38";
      ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
    },
    [canvasSize],
  );

  const drawGrid = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.strokeStyle = "#374151";
      ctx.lineWidth = 1;

      // Calculate appropriate time interval based on zoom level and time range
      let timeInterval = 1; // Default 1 second
      const pixelsPerSecond = PIXELS_PER_SECOND;

      if (pixelsPerSecond > 800) {
        // Very zoomed in - show sub-second intervals
        timeInterval = 0.1; // 100ms intervals
      } else if (pixelsPerSecond > 400) {
        // Zoomed in - show quarter seconds
        timeInterval = 0.25; // 250ms intervals
      } else if (pixelsPerSecond > 200) {
        // Medium zoom - show half seconds
        timeInterval = 0.5; // 500ms intervals
      } else if (maxTime > 300) {
        // 5+ minutes
        timeInterval = 60; // 1 minute intervals
      } else if (maxTime > 120) {
        // 2+ minutes
        timeInterval = 30; // 30 second intervals
      } else if (maxTime > 60) {
        // 1+ minute
        timeInterval = 15; // 15 second intervals
      } else if (maxTime > 30) {
        // 30+ seconds
        timeInterval = 5; // 5 second intervals
      }

      // Vertical grid lines (time markers) with dynamic intervals
      for (let i = 0; i <= Math.ceil(maxTime / timeInterval); i++) {
        const time = i * timeInterval;
        const x = AGENT_WIDTH + panOffset.x + time * PIXELS_PER_SECOND;
        if (x > AGENT_WIDTH && x < canvasSize.width) {
          ctx.beginPath();
          ctx.moveTo(x, TIMELINE_HEIGHT);
          ctx.lineTo(x, canvasSize.height);
          ctx.stroke();
        }
      }

      // Horizontal grid lines (agent lanes)
      let currentY = TIMELINE_HEIGHT + panOffset.y;
      flattenedAgents.forEach((item) => {
        const laneHeight = getLaneHeight(item.agent.id);
        if (currentY > TIMELINE_HEIGHT && currentY < canvasSize.height) {
          ctx.beginPath();
          ctx.moveTo(AGENT_WIDTH, currentY);
          ctx.lineTo(canvasSize.width, currentY);
          ctx.stroke();
        }
        currentY += laneHeight;
      });

      // Draw final bottom line
      if (currentY > TIMELINE_HEIGHT && currentY < canvasSize.height) {
        ctx.beginPath();
        ctx.moveTo(AGENT_WIDTH, currentY);
        ctx.lineTo(canvasSize.width, currentY);
        ctx.stroke();
      }
    },
    [
      maxTime,
      flattenedAgents,
      panOffset,
      canvasSize,
      PIXELS_PER_SECOND,
      getLaneHeight,
    ],
  );

  const drawTimeline = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      // Timeline background
      ctx.fillStyle = "#1a1a3e";
      ctx.fillRect(0, 0, canvasSize.width, TIMELINE_HEIGHT);

      // Timeline border
      ctx.strokeStyle = "#374151";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, TIMELINE_HEIGHT);
      ctx.lineTo(canvasSize.width, TIMELINE_HEIGHT);
      ctx.stroke();

      // Time labels with dynamic formatting
      ctx.fillStyle = "#a1a1aa";
      ctx.font = "11px Inter, sans-serif";
      ctx.textAlign = "center";

      // Use same interval as grid
      let timeInterval = 1; // Default 1 second
      const pixelsPerSecond = PIXELS_PER_SECOND;

      if (pixelsPerSecond > 800) {
        // Very zoomed in - show sub-second intervals
        timeInterval = 0.1; // 100ms intervals
      } else if (pixelsPerSecond > 400) {
        // Zoomed in - show quarter seconds
        timeInterval = 0.25; // 250ms intervals
      } else if (pixelsPerSecond > 200) {
        // Medium zoom - show half seconds
        timeInterval = 0.5; // 500ms intervals
      } else if (maxTime > 300) {
        // 5+ minutes
        timeInterval = 60; // 1 minute intervals
      } else if (maxTime > 120) {
        // 2+ minutes
        timeInterval = 30; // 30 second intervals
      } else if (maxTime > 60) {
        // 1+ minute
        timeInterval = 15; // 15 second intervals
      } else if (maxTime > 30) {
        // 30+ seconds
        timeInterval = 5; // 5 second intervals
      }

      for (let i = 0; i <= Math.ceil(maxTime / timeInterval); i++) {
        const time = i * timeInterval;
        const x = AGENT_WIDTH + panOffset.x + time * PIXELS_PER_SECOND;
        if (x > AGENT_WIDTH && x < canvasSize.width) {
          let label;
          if (time < 1) {
            // Sub-second intervals
            label = `${Math.round(time * 1000)}ms`;
          } else if (time >= 60) {
            const minutes = Math.floor(time / 60);
            const seconds = time % 60;
            if (seconds === 0) {
              label = `${minutes}m`;
            } else if (seconds % 1 === 0) {
              label = `${minutes}m${seconds}s`;
            } else {
              label = `${minutes}m${seconds.toFixed(1)}s`;
            }
          } else if (time % 1 === 0) {
            label = `${time}s`;
          } else {
            label = `${time.toFixed(1)}s`;
          }
          ctx.fillText(label, x, TIMELINE_HEIGHT - 8);
        }
      }

      // Current time indicator with better styling
      const currentTimeX =
        AGENT_WIDTH + panOffset.x + currentTime * PIXELS_PER_SECOND;
      if (currentTimeX > AGENT_WIDTH && currentTimeX < canvasSize.width) {
        ctx.strokeStyle = "#8b5cf6";
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(currentTimeX, 0);
        ctx.lineTo(currentTimeX, canvasSize.height);
        ctx.stroke();
        ctx.setLineDash([]); // Reset line dash

        // Current time label
        ctx.fillStyle = "#8b5cf6";
        ctx.font = "12px Inter, sans-serif";
        ctx.textAlign = "left";
        let currentTimeLabel;
        if (PIXELS_PER_SECOND > 800) {
          // Very zoomed in - show milliseconds
          currentTimeLabel = `${Math.round(currentTime * 1000)}ms`;
        } else if (PIXELS_PER_SECOND > 200) {
          // Medium zoom - show tenths of seconds
          currentTimeLabel = `${currentTime.toFixed(1)}s`;
        } else if (currentTime >= 60) {
          const minutes = Math.floor(currentTime / 60);
          const seconds = currentTime % 60;
          if (seconds % 1 === 0) {
            currentTimeLabel = `${minutes}:${Math.floor(seconds).toString().padStart(2, "0")}`;
          } else {
            currentTimeLabel = `${minutes}:${seconds.toFixed(1).padStart(4, "0")}`;
          }
        } else {
          currentTimeLabel = `${currentTime.toFixed(1)}s`;
        }
        ctx.fillText(currentTimeLabel, currentTimeX + 5, 15);
      }
    },
    [canvasSize, panOffset, maxTime, currentTime, PIXELS_PER_SECOND],
  );

  const drawAgentLabels = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.fillStyle = "#1a1a3e";
      ctx.fillRect(
        0,
        TIMELINE_HEIGHT,
        AGENT_WIDTH,
        canvasSize.height - TIMELINE_HEIGHT,
      );

      // Agent label border
      ctx.strokeStyle = "#374151";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(AGENT_WIDTH, TIMELINE_HEIGHT);
      ctx.lineTo(AGENT_WIDTH, canvasSize.height);
      ctx.stroke();

      flattenedAgents.forEach((item, index) => {
        const agent = item.agent;
        const depth = item.depth;
        const parentId = item.parentId;
        const agentPosition = agentPositions[agent.id];
        if (!agentPosition) return;

        const y = agentPosition.y + panOffset.y;
        const laneHeight = agentPosition.height;

        console.log(`🎨 Drawing agent ${index}:`, {
          agent: agent,
          depth: depth,
          y: y,
          laneHeight: laneHeight,
          visible: y > -laneHeight && y < canvasSize.height + laneHeight,
          agentType: agent.type,
          agentStatus: agent.status,
          color: AGENT_COLORS[agent.type],
          maxEventHeight: Math.max(
            ...events
              .filter((e) => e.agentId === agent.id)
              .map((event) => {
                const eventWidth = Math.max(
                  100,
                  Math.min(400, event.content.length * 6),
                );
                return getEventHeight(event.content, eventWidth);
              }),
            0,
          ),
        });

        if (y > -laneHeight && y < canvasSize.height + laneHeight) {
          const baseIndent = 8;
          const indentSize = 20;
          const currentIndent = baseIndent + depth * indentSize;

          // Draw tree connector lines if there is a parent
          if (parentId) {
            const parentPos = agentPositions[parentId];
            if (parentPos) {
              const parentY = parentPos.y + panOffset.y;
              const parentLaneHeight = parentPos.height;
              const parentCenterY = parentY + parentLaneHeight / 2;
              const childCenterY = y + laneHeight / 2;
              const x1 = currentIndent - 12; // horizontal offset for connector

              ctx.strokeStyle = "#52525b";
              ctx.lineWidth = 1;
              ctx.beginPath();
              // vertical from parent to child
              ctx.moveTo(x1, Math.min(parentCenterY, childCenterY));
              ctx.lineTo(x1, Math.max(parentCenterY, childCenterY));
              // short horizontal into the child label
              ctx.moveTo(x1, childCenterY);
              ctx.lineTo(currentIndent - 4, childCenterY);
              ctx.stroke();
            }
          }

          // Agent color indicator (reflects current status)
          const agentStatus = getAgentCurrentStatus(agent.id);
          ctx.fillStyle = agentStatus.color;
          ctx.fillRect(currentIndent, y + laneHeight / 2 - 2, 4, 4);

          // Agent name with proper indentation
          ctx.fillStyle = "#ffffff";
          ctx.font =
            depth > 0 ? "12px Inter, sans-serif" : "14px Inter, sans-serif";
          ctx.textAlign = "left";

          // Simple text without tree symbols
          ctx.fillText(agent.type, currentIndent + 10, y + laneHeight / 2 + 5);

          // Agent status (dynamic based on latest events)
          // Add pulsing effect for active states
          const isActive =
            agentStatus.status === "running" || agentStatus.status === "active";
          const alpha = isActive ? 0.8 + 0.2 * Math.sin(Date.now() / 500) : 1;

          // Status background
          if (agentStatus.status !== "idle") {
            ctx.fillStyle = `${agentStatus.color}20`;
            ctx.fillRect(currentIndent + 8, y + laneHeight / 2 + 8, 80, 16);
          }

          // Status text
          ctx.fillStyle = isActive
            ? `${agentStatus.color}${Math.floor(alpha * 255)
                .toString(16)
                .padStart(2, "0")}`
            : agentStatus.color;
          ctx.font = "11px Inter, sans-serif";
          ctx.fillText(
            agentStatus.status.toUpperCase(),
            currentIndent + 10,
            y + laneHeight / 2 + 20,
          );
        }
      });
    },
    [
      flattenedAgents,
      agentPositions,
      canvasSize,
      panOffset,
      getAgentCurrentStatus,
      events,
    ],
  );

  const drawEvents = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      visibleEvents.forEach((event) => {
        const agentPosition = agentPositions[event.agentId];
        if (!agentPosition) return;

        const eventX =
          AGENT_WIDTH + panOffset.x + event.timestamp * PIXELS_PER_SECOND;
        const eventWidth = Math.max(
          100,
          Math.min(400, event.content.length * 6),
        );
        const eventHeight = getEventHeight(event.content, eventWidth);
        // Better centering with minimum offset from top
        const verticalPadding = Math.max(
          20,
          (agentPosition.height - eventHeight) / 2,
        );
        const eventY = agentPosition.y + panOffset.y + verticalPadding;

        // Skip if outside visible area
        if (
          eventX + eventWidth < AGENT_WIDTH ||
          eventX > canvasSize.width ||
          eventY + eventHeight < TIMELINE_HEIGHT ||
          eventY > canvasSize.height
        ) {
          return;
        }

        // Event background
        const isHovered = hoveredEvent?.id === event.id;
        const isHighlighted = highlightedEventId === event.id;
        ctx.fillStyle = EVENT_COLORS[event.type] || "#6b7280";

        if (isHighlighted) {
          // Add pulsing highlight effect for selected event
          const pulse = 0.7 + 0.3 * Math.sin(Date.now() / 200);
          ctx.shadowColor = "#3b82f6";
          ctx.shadowBlur = 15 * pulse;
          // Make the event slightly brighter when highlighted
          const originalColor = ctx.fillStyle;
          ctx.fillStyle = isHovered ? originalColor : `${originalColor}dd`;
        } else if (isHovered) {
          // Add glow effect for hovered event
          ctx.shadowColor = ctx.fillStyle;
          ctx.shadowBlur = 10;
        }

        // Rounded rectangle
        ctx.beginPath();
        ctx.roundRect(eventX, eventY, eventWidth, eventHeight, 12);
        ctx.fill();

        if (isHovered || isHighlighted) {
          ctx.shadowBlur = 0;
        }

        // Add highlight border for selected event
        if (isHighlighted) {
          ctx.strokeStyle = "#3b82f6";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.roundRect(eventX, eventY, eventWidth, eventHeight, 12);
          ctx.stroke();
        }

        // Event text with word wrapping
        ctx.fillStyle = "#ffffff";
        ctx.font = "12px Inter, sans-serif";
        ctx.textAlign = "left";

        // Word wrap the text
        const words = event.content.split(" ");
        const maxWidth = eventWidth - 16; // Account for padding
        const lines = [];
        let currentLine = "";

        words.forEach((word) => {
          const testLine = currentLine + (currentLine ? " " : "") + word;
          const testWidth = ctx.measureText(testLine).width;

          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        });

        if (currentLine) {
          lines.push(currentLine);
        }

        // Draw each line
        lines.forEach((line, index) => {
          const lineY = eventY + 16 + index * LINE_HEIGHT;
          ctx.fillText(line, eventX + 8, lineY);
        });

        // Event type indicator
        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.font = "10px Inter, sans-serif";
        ctx.textAlign = "right";
        ctx.fillText(event.type, eventX + eventWidth - 8, eventY + 12);
      });
    },
    [
      visibleEvents,
      agentPositions,
      panOffset,
      canvasSize,
      hoveredEvent,
      highlightedEventId,
      PIXELS_PER_SECOND,
      getEventHeight,
    ],
  );

  // Main render function
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);

    // Draw layers
    drawBackground(ctx);
    drawGrid(ctx);
    drawEvents(ctx);
    drawTimeline(ctx);
    drawAgentLabels(ctx);
  }, [
    canvasSize,
    drawBackground,
    drawGrid,
    drawEvents,
    drawTimeline,
    drawAgentLabels,
  ]);

  // Effects
  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    render();
  }, [render]);

  // Continuous rendering for real-time updates
  useEffect(() => {
    const animate = () => {
      render();
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [render]);

  return (
    <div
      ref={containerRef}
      className={`waterfall-container relative ${className} h-full flex`}
    >
      {/* Main waterfall canvas area */}
      <div className="flex-1 relative">
        <canvas
          ref={canvasRef}
          className="waterfall-canvas h-full w-full"
          role="presentation"
          aria-label="AI Agent Waterfall Visualization"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          onWheel={handleWheel}
        />

        {/* Auto-scroll toggle */}
        <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`px-3 py-1 text-xs rounded ${
              autoScroll
                ? "bg-primary-600 text-white"
                : "bg-gray-600 text-gray-300"
            }`}
            title={autoScroll ? "Disable auto-scroll" : "Enable auto-scroll"}
          >
            Auto-follow: {autoScroll ? "ON" : "OFF"}
          </button>
          <div className="text-xs text-gray-300 bg-gray-800 px-2 py-1 rounded">
            <div>
              Time: {zoomX}% | Rows: {zoomY}%
            </div>
            <div className="text-[10px] text-gray-400">
              Duration:{" "}
              {maxTime >= 60
                ? `${Math.floor(maxTime / 60)}:${Math.floor(maxTime % 60)
                    .toString()
                    .padStart(2, "0")}`
                : `${maxTime}s`}
            </div>
            <div className="text-[10px] text-gray-400">
              {PIXELS_PER_SECOND > 800
                ? "Precision: 100ms"
                : PIXELS_PER_SECOND > 400
                  ? "Precision: 250ms"
                  : PIXELS_PER_SECOND > 200
                    ? "Precision: 500ms"
                    : maxTime > 300
                      ? "Precision: 1min"
                      : maxTime > 120
                        ? "Precision: 30s"
                        : maxTime > 60
                          ? "Precision: 15s"
                          : maxTime > 30
                            ? "Precision: 5s"
                            : "Precision: 1s"}
            </div>
            <div className="text-[10px] text-gray-400 mt-1">
              Scroll: rows | Shift+Scroll: time | Ctrl+Scroll: both
            </div>
            <div className="text-[10px] text-blue-400 mt-1">
              💡 Click messages in chat to center them
            </div>
          </div>

          {/* Centering Status */}
          {centeringStatus && (
            <div className="text-xs text-blue-300 bg-blue-900/50 px-3 py-2 rounded border border-blue-500/30">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span>{centeringStatus}</span>
              </div>
            </div>
          )}

          {/* Agent Status Legend */}
          <div className="text-xs text-gray-300 bg-gray-800 px-2 py-1 rounded">
            <div className="text-[10px] text-gray-400 mb-1">Agent Status:</div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded"></div>
                <span className="text-[10px]">RUNNING</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-amber-500 rounded"></div>
                <span className="text-[10px]">ACTIVE</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded"></div>
                <span className="text-[10px]">COMPLETE</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-500 rounded"></div>
                <span className="text-[10px]">BUSY</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-red-500 rounded"></div>
                <span className="text-[10px]">ERROR</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-500 rounded"></div>
                <span className="text-[10px]">IDLE</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tooltip */}
        {hoveredEvent && (
          <div
            className="tooltip absolute bg-gray-900 text-white p-3 rounded-lg shadow-xl pointer-events-auto z-20 max-w-md border border-gray-700"
            style={{
              left: mousePosition.x + 10,
              top: mousePosition.y - 10,
              transform:
                mousePosition.x > canvasSize.width / 2
                  ? "translateX(-100%)"
                  : "none",
            }}
          >
            <div className="font-semibold text-white text-sm">
              {hoveredEvent.type}
            </div>
            <div className="mt-2">
              <MarkdownRenderer content={hoveredEvent.content} compact={false} />
            </div>
            <div className="text-xs text-gray-400 mt-3 pt-2 border-t border-gray-700">
              <div>Time: {hoveredEvent.timestamp.toFixed(2)}s</div>
              {hoveredEvent.toolName && (
                <div className="mt-1">Tool: {hoveredEvent.toolName}</div>
              )}
              {hoveredEvent.status && (
                <div className="mt-1">Status: {hoveredEvent.status}</div>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              {hoveredEvent.status === "error" && (
                <button
                  className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    const agentId = hoveredEvent.agentId;
                    onSendMessage?.("retry", [agentId]);
                  }}
                >
                  Retry
                </button>
              )}
              <button
                className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-500"
                onClick={(e) => {
                  e.stopPropagation();
                  const agentId = hoveredEvent.agentId;
                  onSendMessage?.("Please handle this.", [agentId]);
                }}
              >
                Message Agent
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Chat Client */}
      {showChatClient && (
        <ResizablePanel
          position="right"
          defaultWidth={chatClientWidth}
          minWidth={250}
          maxWidth={600}
          onWidthChange={handleChatWidthChange}
          className="bg-gray-900 border-l border-gray-700"
        >
          {!selectedAgentId ? (
            <ChatClient
              events={events}
              agents={agents}
              onSendMessage={onSendMessage}
              onMessageSelect={centralizeEvent}
              sessions={sessions}
              onLoadSession={onLoadSession}
              onNewChat={onNewChat}
              isLoadingSessions={isLoadingSessions}
              className="h-full"
            />
          ) : (
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between p-3 border-b border-gray-700">
                <div className="text-sm font-semibold">
                  Agent Details
                </div>
                <button
                  className="text-xs text-gray-300 hover:text-white"
                  onClick={() => setSelectedAgentId(null)}
                >
                  Close
                </button>
              </div>
              <div className="flex-1 overflow-auto p-3 space-y-3">
                {(() => {
                  const agent = agents.find((a) => a.id === selectedAgentId);
                  if (!agent) return <div className="text-sm text-gray-400">Agent not found.</div>;

                  // Gather recent messages and tool activity for the agent
                  const agentEvents = events.filter((e) => e.agentId === selectedAgentId);
                  const recentMessages = agentEvents.filter((e) => e.type === "message").slice(-10);
                  const recentTools = agentEvents.filter((e) => e.type === "toolCall" || e.type === "toolResult").slice(-10);

                  return (
                    <>
                      <div>
                        <div className="text-xs text-gray-400">Type</div>
                        <div className="text-sm text-white">{agent.type}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">Objective</div>
                        <div className="text-sm text-white whitespace-pre-wrap">{agent.objective}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">System / Role</div>
                        <div className="text-sm text-gray-300">
                          This agent was dispatched with a role of {""}{agent.type}{""}.
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">Recent Tool Activity</div>
                        <div className="space-y-2">
                          {recentTools.length === 0 ? (
                            <div className="text-xs text-gray-500">No tool activity yet.</div>
                          ) : (
                            recentTools.map((ev) => (
                              <div key={ev.id} className="text-xs text-gray-300">
                                <div className="text-gray-400">[{ev.type}]</div>
                                <div className="whitespace-pre-wrap">{ev.content}</div>
                                {ev.toolName && (
                                  <div className="text-gray-500">Tool: {ev.toolName}</div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400">Recent Messages</div>
                        <div className="space-y-2">
                          {recentMessages.length === 0 ? (
                            <div className="text-xs text-gray-500">No messages yet.</div>
                          ) : (
                            recentMessages.map((ev) => {
                              const isUserMessage = ev.metadata?.from === "user";
                              return (
                                <div 
                                  key={ev.id} 
                                  className={`text-sm text-gray-200 rounded-lg p-3 break-words overflow-wrap-anywhere ${
                                    isUserMessage 
                                      ? "bg-blue-600/20 border border-blue-500/30 ml-8" 
                                      : "bg-gray-700/50"
                                  }`}
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="text-[10px] text-gray-400">
                                      {new Date(ev.timestamp * 1000).toLocaleTimeString()}
                                    </div>
                                    {isUserMessage && (
                                      <div className="text-[10px] text-blue-300 font-medium">
                                        👤 You
                                      </div>
                                    )}
                                  </div>
                                  <div className="whitespace-pre-wrap">
                                    <MarkdownRenderer content={ev.content} compact={false} />
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="p-3 border-t border-gray-700 flex gap-2">
                <input
                  value={agentQuickMessage}
                  onChange={(e) => setAgentQuickMessage(e.target.value)}
                  placeholder="Send a message to this agent…"
                  className="flex-1 bg-gray-800 text-sm text-white px-2 py-1 rounded border border-gray-700"
                />
                <button
                  className="text-xs px-3 py-1 rounded bg-primary-600 hover:bg-primary-500"
                  onClick={() => {
                    if (!selectedAgentId || !agentQuickMessage.trim()) return;
                    onSendMessage?.(agentQuickMessage.trim(), [selectedAgentId]);
                    setAgentQuickMessage("");
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </ResizablePanel>
      )}
    </div>
  );
});

WaterfallVisualization.displayName = 'WaterfallVisualization';
